import React, { useState, useEffect, useCallback } from 'react';
import {
  Loader2,
  RefreshCw,
  Trash2,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Clock,
  Play,
  Eye,
  Search,
  X,
} from 'lucide-react';
import { apiService } from '../../services/api';
import CustomSelect from '../common/CustomSelect';
import type { QueueItem, QueueItemStatus } from '../../types';
import Tooltip from '../common/Tooltip';
import ConfirmationDialog from '../common/ConfirmationDialog';
import { resolveDotPath } from '../../utils/payloadPath';

interface QueueItemsListProps {
  queueId: number;
  /**
   * Optional priority field on the parent queue. When set, the items list
   * shows an "Ordering: Priority by <field>" hint; otherwise "Ordering: FIFO".
   */
  priorityField?: string | null;
  /**
   * AC-1325: when true, hide the status filter dropdown and the Status
   * column — error queues only contain FAILED items so those controls are
   * redundant. Also defaults the filter to 'all' so items are visible on
   * first render instead of defaulting to (empty) pending.
   */
  isErrorQueue?: boolean;
  /**
   * AC-1479: Optional list of payload dot-paths to project as columns in
   * place of the catch-all truncated-JSON Payload column. When undefined or
   * empty, the legacy single Payload column is rendered (back-compat).
   */
  displayPayloadFields?: string[];
  onViewItem?: (item: QueueItem) => void;
  onRefresh?: () => void;
}

/**
 * QueueItemsList - Displays queue items with pagination, filtering, and actions
 *
 * AC-435: Queue Items Viewing UI
 */
const QueueItemsList: React.FC<QueueItemsListProps> = ({
  queueId,
  priorityField,
  isErrorQueue = false,
  displayPayloadFields,
  onViewItem,
  onRefresh,
}) => {
  const projectedFields = displayPayloadFields ?? [];
  const hasProjectedFields = projectedFields.length > 0;
  // State
  const [items, setItems] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // AC-1311: default to pending so the Queue Details page opens focused on
  // actionable work rather than the complete history.
  // AC-1325: on error queues every row is FAILED, so default to 'all' so
  // the operator sees the backlog immediately.
  const [statusFilter, setStatusFilter] = useState<QueueItemStatus | 'all'>(
    isErrorQueue ? 'all' : 'pending'
  );
  const [skip, setSkip] = useState(0);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [actionLoading, setActionLoading] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  // AC-1590: Search input is debounced (300ms) so each keystroke does not
  // hit the backend. `searchInput` drives the input value; `searchTerm` is
  // the debounced value used as the actual fetch parameter.
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const limit = 20;

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setSkip(0);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  // Fetch items
  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: {
        offset: number;
        limit: number;
        status?: string;
        search?: string;
      } = { offset: skip, limit };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (searchTerm) {
        params.search = searchTerm;
      }
      const fetchedItems = await apiService.getQueueItems(queueId, params);
      setItems(fetchedItems);
    } catch (err) {
      console.error('Failed to fetch queue items:', err);
      setError('Failed to load queue items. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [queueId, skip, statusFilter, searchTerm]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Handle refresh
  const handleRefresh = () => {
    fetchItems();
    onRefresh?.();
  };

  // Handle status filter change
  const handleStatusFilterChange = (status: QueueItemStatus | 'all') => {
    setStatusFilter(status);
    setSkip(0);
    setSelectedItems(new Set());
  };

  // Handle item selection
  const handleSelectItem = (itemId: string) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedItems.size === items.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(items.map(item => item.item_id)));
    }
  };

  // Handle delete item
  const confirmDeleteItem = async () => {
    if (!deleteItemId) return;
    const itemId = deleteItemId;
    setActionLoading(true);
    try {
      await apiService.deleteQueueItem(queueId, itemId);
      await fetchItems();
      onRefresh?.();
    } catch (err) {
      console.error('Failed to delete item:', err);
      alert('Failed to delete item. Please try again.');
    } finally {
      setActionLoading(false);
      setDeleteItemId(null);
    }
  };

  // Handle retry item
  const handleRetryItem = async (itemId: string) => {
    setActionLoading(true);
    try {
      await apiService.retryQueueItem(queueId, itemId);
      await fetchItems();
      onRefresh?.();
    } catch (err) {
      console.error('Failed to retry item:', err);
      alert('Failed to retry item. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle bulk delete
  const confirmBulkDelete = async () => {
    if (selectedItems.size === 0) {
      setShowBulkDeleteConfirm(false);
      return;
    }
    setActionLoading(true);
    try {
      await apiService.bulkDeleteQueueItems(queueId, Array.from(selectedItems));
      setSelectedItems(new Set());
      await fetchItems();
      onRefresh?.();
    } catch (err) {
      console.error('Failed to bulk delete items:', err);
      alert('Failed to delete items. Please try again.');
    } finally {
      setActionLoading(false);
      setShowBulkDeleteConfirm(false);
    }
  };

  // Handle bulk retry
  const handleBulkRetry = async () => {
    if (selectedItems.size === 0) return;

    setActionLoading(true);
    try {
      await apiService.bulkRetryQueueItems(queueId, Array.from(selectedItems));
      setSelectedItems(new Set());
      await fetchItems();
      onRefresh?.();
    } catch (err) {
      console.error('Failed to bulk retry items:', err);
      alert('Failed to retry items. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  // Get status icon
  const getStatusIcon = (status: QueueItemStatus) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" />;
      case 'processing':
        return <Play className="w-4 h-4 text-primary-500" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  // Get status badge class
  const getStatusBadgeClass = (status: QueueItemStatus) => {
    switch (status) {
      case 'pending':
        return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
      case 'processing':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
      case 'completed':
        return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
      case 'failed':
        return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    }
  };

  // Format payload preview
  const formatPayloadPreview = (payload: Record<string, any>): string => {
    const str = JSON.stringify(payload);
    return str.length > 50 ? str.substring(0, 50) + '...' : str;
  };

  // AC-1479: Render the value at a single dot-path for the projected-column
  // mode. Returns the em-dash string for missing/null/undefined so the cell
  // visually distinguishes "no data" from a falsy primitive (0, false, "").
  const renderProjectedCell = (
    payload: Record<string, any>,
    path: string,
  ): string => {
    const value = resolveDotPath(payload, path);
    if (value === null || value === undefined) return '—'; // em-dash
    if (typeof value === 'object') {
      const json = JSON.stringify(value);
      return json.length > 50 ? json.substring(0, 47) + '...' : json;
    }
    return String(value);
  };

  // Pagination
  const hasPrevPage = skip > 0;
  const hasNextPage = items.length === limit;

  return (
    <div className="bg-white dark:bg-cap-dark shadow rounded-lg">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900 dark:text-warm-white">Queue Items</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Ordering: {priorityField ? (
                <>Priority by <code className="font-mono">{priorityField}</code> (highest first)</>
              ) : (
                <>FIFO (oldest first)</>
              )}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {/* AC-1590: Payload search input. Debounced (300ms) so each
                keystroke does not hit the backend. Backend filters via
                ILIKE on the queue's configured display_payload_fields
                only - so the input is hidden when no display fields are
                configured (there is nothing visible for the operator to
                filter against). */}
            {hasProjectedFields && (
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder={`Filter ${projectedFields.join(', ')}...`}
                  aria-label="Filter queue items by displayed payload fields"
                  title={`Filters by case-insensitive substring across: ${projectedFields.join(', ')}`}
                  className="pl-8 pr-7 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-warm-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 w-56"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={() => setSearchInput('')}
                    aria-label="Clear filter"
                    className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}

            {/* Status Filter — AC-1325: hidden on error queues where all
                items are FAILED and the filter would be redundant. */}
            {!isErrorQueue && (
              <CustomSelect
                value={statusFilter}
                onChange={(value) => handleStatusFilterChange(value as QueueItemStatus | 'all')}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'processing', label: 'Processing' },
                  { value: 'completed', label: 'Completed' },
                  { value: 'failed', label: 'Failed' },
                ]}
                aria-label="Status Filter"
              />
            )}

            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={loading || actionLoading}
              className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedItems.size > 0 && (
          <div className="mt-3 flex items-center space-x-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {selectedItems.size} selected
            </span>
            <button
              onClick={() => setShowBulkDeleteConfirm(true)}
              disabled={actionLoading}
              className="inline-flex items-center px-2 py-1 text-xs text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 rounded hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Delete
            </button>
            <button
              onClick={handleBulkRetry}
              disabled={actionLoading}
              className="inline-flex items-center px-2 py-1 text-xs text-primary-600 dark:text-blue-400 border border-primary-300 dark:border-blue-700 rounded hover:bg-primary-50 dark:hover:bg-blue-900/20 disabled:opacity-50"
            >
              <RotateCcw className="w-3 h-3 mr-1" />
              Retry
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
            <span className="ml-2 text-gray-600 dark:text-gray-400">Loading items...</span>
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-600">
            <AlertCircle className="w-8 h-8 mx-auto mb-2" />
            <p>{error}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p className="text-lg font-medium">
              {searchTerm ? 'No items match the filter' : 'No items in queue'}
            </p>
            <p className="text-sm mt-1">
              {searchTerm
                ? `No payloads contain "${searchTerm}". Try a different search term.`
                : statusFilter !== 'all'
                ? `No ${statusFilter} items found. Try a different filter.`
                : 'Items will appear here when added to the queue.'}
            </p>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-3 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedItems.size === items.length && items.length > 0}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                      />
                    </th>
                    {/* Status column — AC-1325: hidden on error queues
                        where every row is FAILED by construction. */}
                    {!isErrorQueue && (
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                    )}
                    {hasProjectedFields ? (
                      // AC-1479: One column per projected dot-path.
                      projectedFields.map((path) => (
                        <th
                          key={`payload-col-${path}`}
                          className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider font-mono"
                        >
                          {path}
                        </th>
                      ))
                    ) : (
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Payload
                      </th>
                    )}
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      <Tooltip content="Lifetime count of times this item has been moved into the error queue. Independent of the current-pass retry_count, which resets when an operator retries.">
                        <span>Failures</span>
                      </Tooltip>
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {items.map((item) => (
                    <tr key={item.item_id} className="hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900">
                      <td className="px-3 py-4">
                        <input
                          type="checkbox"
                          checked={selectedItems.has(item.item_id)}
                          onChange={() => handleSelectItem(item.item_id)}
                          className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                        />
                      </td>
                      {!isErrorQueue && (
                        <td className="px-3 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(item.status)}`}>
                            {getStatusIcon(item.status)}
                            <span className="ml-1 capitalize">{item.status}</span>
                          </span>
                        </td>
                      )}
                      {hasProjectedFields ? (
                        // AC-1479: One cell per projected dot-path.
                        projectedFields.map((path) => {
                          const rendered = renderProjectedCell(item.payload, path);
                          const isMissing = rendered === '—';
                          return (
                            <td
                              key={`payload-cell-${item.item_id}-${path}`}
                              className="px-3 py-4 text-sm"
                            >
                              {isMissing ? (
                                <span className="text-gray-400 dark:text-gray-500">
                                  —
                                </span>
                              ) : (
                                <code className="text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono">
                                  {rendered}
                                </code>
                              )}
                            </td>
                          );
                        })
                      ) : (
                        <td className="px-3 py-4">
                          <code className="text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                            {formatPayloadPreview(item.payload)}
                          </code>
                        </td>
                      )}
                      <td className="px-3 py-4 whitespace-nowrap text-sm">
                        {item.priority && item.priority !== 0 ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 font-mono">
                            {item.priority}
                          </span>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500">—</span>
                        )}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm">
                        {(item.total_failure_count ?? 0) > 0 ? (
                          <Tooltip content={`Moved to the error queue ${item.total_failure_count} time${item.total_failure_count === 1 ? '' : 's'}`}>
                            <span
                              className={
                                (item.total_failure_count ?? 0) >= 3
                                  ? 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 font-mono'
                                  : 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 font-mono'
                              }
                            >
                              {item.total_failure_count}
                            </span>
                          </Tooltip>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500">—</span>
                        )}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {new Date(item.created_at).toLocaleString()}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {onViewItem && (
                            <Tooltip content="View details">
                              <button
                                onClick={() => onViewItem(item)}
                                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            </Tooltip>
                          )}
                          {item.status === 'failed' && (
                            <Tooltip content="Retry">
                              <button
                                onClick={() => handleRetryItem(item.item_id)}
                                disabled={actionLoading}
                                className="text-primary-500 hover:text-primary-700 disabled:opacity-50"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </button>
                            </Tooltip>
                          )}
                          <Tooltip content="Delete">
                            <button
                              onClick={() => setDeleteItemId(item.item_id)}
                              disabled={actionLoading}
                              className="text-red-500 hover:text-red-700 disabled:opacity-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Showing {skip + 1} - {skip + items.length} items
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setSkip(Math.max(0, skip - limit))}
                  disabled={!hasPrevPage || loading}
                  className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </button>
                <button
                  onClick={() => setSkip(skip + limit)}
                  disabled={!hasNextPage || loading}
                  className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {deleteItemId && (
        <ConfirmationDialog
          title="Delete queue item?"
          message="Are you sure you want to delete this item? This action cannot be undone."
          confirmText="Delete"
          confirmVariant="danger"
          isLoading={actionLoading}
          onConfirm={confirmDeleteItem}
          onCancel={() => setDeleteItemId(null)}
        />
      )}
      {showBulkDeleteConfirm && (
        <ConfirmationDialog
          title={`Delete ${selectedItems.size} item${selectedItems.size === 1 ? '' : 's'}?`}
          message={`Are you sure you want to delete ${selectedItems.size} item${selectedItems.size === 1 ? '' : 's'}? This action cannot be undone.`}
          confirmText="Delete"
          confirmVariant="danger"
          isLoading={actionLoading}
          onConfirm={confirmBulkDelete}
          onCancel={() => setShowBulkDeleteConfirm(false)}
        />
      )}
    </div>
  );
};

export default QueueItemsList;
